import React, { useEffect } from 'react';
import { Route, useHistory, useLocation } from 'react-router';
import LoadingIndicator from 'Shared/LoadingIndicator';
import { useRegistrationUserDetails } from './hooks/useRegistrationUserDetails';
import {
  RegistrationFlow,
  RegistrationMethodMap,
  SubRoutes,
} from './constants';
import { getPath, redirectIfNotInitialPath } from './registrationFlowHelpers';
import { RegistrationFlowState } from './types';

export const TypedRegistrationFlow = <T extends RegistrationFlow>({
  registrationFlow,
  loading,
  onEnd,
  error,
  initialFlowDetails,
  parentPath,
  skippedSubroutes,
}: {
  registrationFlow: T;
  onEnd(args: RegistrationFlowState[T]): Promise<void>;
  error?: string;
  loading?: boolean;
  initialFlowDetails?: RegistrationFlowState[T];
  parentPath: string;
  skippedSubroutes?: SubRoutes[];
}) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { userDetails, updateUserDetails, validation } =
    useRegistrationUserDetails(registrationFlow);
  const possibleSearch = search || '';

  const subRoutes = RegistrationMethodMap[registrationFlow];

  // If a user lands on a nested step in the flow directly (e.g. via link or refresh),
  // redirect them to the beginning of the flow.
  useEffect(() => {
    redirectIfNotInitialPath({
      pathname,
      parentPath,
      search: possibleSearch,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialFlowDetails) {
      updateUserDetails(initialFlowDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFlowDetails]);

  if (!userDetails || Object.keys(userDetails).length === 0 || loading) {
    return <LoadingIndicator />;
  }

  const navigateToNextPage = (page: SubRoutes) => {
    history.push(`${parentPath}/${page}${possibleSearch}`);
  };

  const getIsInitialValid = (subRoute: SubRoutes) => {
    const component = subRoutes.find(({ route }) => route === subRoute);
    const allowedProps = component?.fields || [];

    return allowedProps.every(
      prop =>
        validation.errors[prop as keyof typeof validation.errors] === undefined,
    );
  };

  const filteredRoutes = subRoutes.filter(
    ({ route }) => !skippedSubroutes?.includes(route),
  );

  return (
    <>
      {filteredRoutes.map(({ component, route }, index) => {
        const Component = component;
        const isLast = index === filteredRoutes.length - 1;
        const nextRoute = filteredRoutes[index + 1]?.route;

        return (
          <Route
            key={route}
            path={getPath(
              route,
              filteredRoutes.map(r => r.route),
              parentPath,
            )}
            exact={true}
          >
            <Component
              initialValues={userDetails}
              skipExternalValidation
              isInitialValid={getIsInitialValid(route)}
              error={error}
              onSubmit={async args => {
                const updatedUserDetails = {
                  ...userDetails,
                  ...args,
                };

                updateUserDetails(args);

                if (!isLast) {
                  navigateToNextPage(nextRoute);
                } else {
                  await onEnd({
                    ...updatedUserDetails,
                  });
                }
              }}
            />
          </Route>
        );
      })}
    </>
  );
};
